import {
  ChoiceInput,
  Divider,
  Flex,
  Input,
  Steps,
  Text,
  TextToolTip,
} from '@components';
import { useUpdateUserInfoMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { bankDetailsFormSchema } from '@validationSchemas/investment';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './BankDetails.styles';
import { BankDetailsFormData } from './BankDetails.types';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { ibanRegex } from '@utils/validation/consts';
import { InvestmentContainer } from '../Investment.styles';
import { TOTAL_STEPS } from '../Investment.consts';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';
import { useEffect } from 'react';

const BankDetails: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();

  const { productId } = useParams();

  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const {
    register,
    handleSubmit,
    control,
    watch,
    clearErrors,
    setValue,
    formState: { errors, isValid },
  } = useForm<BankDetailsFormData>({
    resolver: yupResolver(bankDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      accountHolderName: user.bankDetails?.accountHolderName,
      bankName: user.bankDetails?.bankName,
      bankLocation: user.bankDetails?.bankLocation,
      isNominee:
        user.bankDetails?.soleAccountOwner !== undefined
          ? !user.bankDetails?.soleAccountOwner
          : undefined,
      ...(user.bankDetails?.accountNumber &&
        !user.bankDetails?.iban && {
          accountNumber: user.bankDetails?.accountNumber,
          swiftOrBic: user.bankDetails?.swiftOrBic,
        }),
      ...(user.bankDetails?.iban &&
        !user.bankDetails?.accountNumber && {
          iban: user.bankDetails?.iban,
        }),
    },
  });

  const iban = watch('iban');
  const bankAccountNumber = watch('accountNumber');
  const swiftOrBic = watch('swiftOrBic');

  useEffect(() => {
    if (iban) {
      setValue('accountNumber', '');
      setValue('swiftOrBic', '');
      if (errors.accountNumber) clearErrors('accountNumber');
      if (errors.swiftOrBic) clearErrors('swiftOrBic');
    }
    if (bankAccountNumber || swiftOrBic) {
      setValue('iban', '');
      if (errors.iban) clearErrors('iban');
    }
  }, [
    iban,
    bankAccountNumber,
    swiftOrBic,
    setValue,
    clearErrors,
    errors.accountNumber,
    errors.swiftOrBic,
    errors.iban,
  ]);

  const onSubmit = async ({ isNominee, ...data }: BankDetailsFormData) => {
    try {
      await updateUserInfoMutation({
        ...user,
        bankDetails: {
          ...user.bankDetails,
          ...data,
          soleAccountOwner: !isNominee,
        },
      }).unwrap();

      if (isNominee) {
        navigate(`/investment-subscription/${productId}/unavailable`, {
          state: {
            progress: 6,
            type: 'nominee',
            previousPathName: 'bank-details',
          },
          replace: true,
        });
      } else {
        navigate(`/investment-subscription/${productId}/questionnaire`, {
          state: {
            progress: 6,
          },
          replace: true,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (user.personalDetailsLocked) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }
  return (
    <InvestmentContainer data-testid='bank-details-container'>
      <ContentContainer>
        <Steps currentStep={6} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          mt={5}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.bankDetails.bankDetails' />
        </Text>
        <Flex gap={16} mt={16} flexDirection='column'>
          <Input
            label={intl.formatMessage({
              id: 'investment.bankDetails.accountHolderName',
            })}
            fullWidth
            inputProps={{
              'data-testid': 'account-holder-name',
            }}
            {...registerInput(register, 'accountHolderName')}
            {...getErrorProps(intl, errors.accountHolderName)}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.bankDetails.bankName',
            })}
            fullWidth
            inputProps={{
              'data-testid': 'bank-name',
            }}
            {...registerInput(register, 'bankName')}
            {...getErrorProps(intl, errors.bankName)}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.bankDetails.bankLocation',
            })}
            fullWidth
            inputProps={{
              'data-testid': 'bank-location',
            }}
            {...registerInput(register, 'bankLocation')}
            {...getErrorProps(intl, errors.bankLocation)}
          />
          <Flex flexDirection='column' rowGap={10}>
            <Text
              variant='h5'
              fontWeight='fontWeightSemiBold'
              color={theme.palette.black.A100}
            >
              <FormattedMessage id='investment.bankDetails.bankAccountIdentifier' />
            </Text>
            <Controller
              control={control}
              name='iban'
              render={({ field: { onChange, ...restProps } }) => (
                <Input
                  label={intl.formatMessage({
                    id: 'investment.bankDetails.iban',
                  })}
                  fullWidth
                  inputProps={{
                    'data-testid': 'iban',
                    sx: {
                      textTransform: 'uppercase',
                    },
                  }}
                  disabled={!!bankAccountNumber || !!swiftOrBic}
                  onChange={(ev) => {
                    return onChange(
                      ev.target.value
                        ?.replace(ibanRegex, '')
                        ?.replace(/(.{4})/g, '$1 ')
                        ?.trim()
                    );
                  }}
                  {...restProps}
                />
              )}
            />
            <Box>
              <Divider>OR</Divider>
            </Box>
            <Flex flexDirection='column' gap={16}>
              <Input
                label={intl.formatMessage({
                  id: 'investment.bankDetails.bankAccountNumber',
                })}
                inputProps={{
                  'data-testid': 'bank-account-number',
                  sx: {
                    textTransform: 'uppercase',
                  },
                }}
                fullWidth
                disabled={!!iban}
                {...registerInput(register, 'accountNumber')}
                {...getErrorProps(intl, errors.accountNumber)}
              />
              <Input
                label={intl.formatMessage({
                  id: 'investment.bankDetails.swiftAndBic',
                })}
                inputProps={{
                  'data-testid': 'swift-Bic',
                  sx: {
                    textTransform: 'uppercase',
                  },
                }}
                fullWidth
                disabled={!!iban}
                {...registerInput(register, 'swiftOrBic')}
                {...getErrorProps(intl, errors.swiftOrBic)}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex mt={16} mb={5}>
          <TextToolTip
            text={intl.formatMessage({
              id: 'investment.bankDetails.soleBeneficialOwnerAccount',
            })}
            toolTipText={intl.formatMessage({
              id: 'investment.bankDetails.soleBeneficialOwnerAccountTooltipMessage',
            })}
          />
        </Flex>
        <Controller
          control={control}
          name='isNominee'
          render={({ field: { ref, value, ...restProps } }) => (
            <ChoiceInput
              direction='row'
              fullWidth
              choices={yesNoChoices}
              defaultValues={value !== undefined ? [value] : undefined}
              {...restProps}
            />
          )}
        />
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/tax-details`, {
              state: {
                progress: 4,
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(BankDetails);
